import { Injectable } from '@angular/core';
import { defer, delay, Observable, of } from 'rxjs';

import type { TableQuery } from '../../../shared/components/generic-table/models/table-query.model';
import type {
  CaseInvestigationTrackerRow,
  CaseStatus,
  EmployeeStatus,
} from '../models/case-investigation-tracker-row.model';
import type {
  CaseInvestigationTrackerClient,
  TableDataResponse,
} from '../data-access/case-investigation-tracker-client.token';
import { filterCaseInvestigationTrackerRowsBySearch } from '../utils/case-investigation-tracker-search.utils';

@Injectable({ providedIn: 'root' })
export class CaseInvestigationTrackerMockService implements CaseInvestigationTrackerClient {
  private readonly allRows = buildMockRows();

  getRows(query: TableQuery): Observable<TableDataResponse<CaseInvestigationTrackerRow>> {
    return defer(() => {
      const normalized = {
        ...query,
        page: Math.max(0, query.page ?? 0),
        rows: Math.max(1, query.rows ?? 10),
      };

      const afterSearch = filterCaseInvestigationTrackerRowsBySearch(this.allRows, normalized.search);
      const afterFilters = this.applyColumnFilters(afterSearch, normalized.filters);
      const sorted = this.applySort(
        afterFilters,
        normalized.sortField,
        normalized.sortOrder
      );
      const start = normalized.page * normalized.rows;
      const pageData = sorted.slice(start, start + normalized.rows);

      return of({
        data: pageData,
        totalRecords: sorted.length,
      } satisfies TableDataResponse<CaseInvestigationTrackerRow>);
    }).pipe(delay(300));
  }

  private applyColumnFilters(
    rows: readonly CaseInvestigationTrackerRow[],
    filters: Record<string, string> | undefined
  ): CaseInvestigationTrackerRow[] {
    if (!filters || Object.keys(filters).length === 0) return [...rows];
    return rows.filter((row) =>
      Object.entries(filters).every(([field, filterValue]) => {
        const term = (filterValue ?? '').trim().toLowerCase();
        if (!term) return true;
        const cell = row[field];
        const cellStr = cell != null ? String(cell).toLowerCase() : '';
        return cellStr.includes(term);
      })
    );
  }

  private applySort(
    rows: CaseInvestigationTrackerRow[],
    sortField: string | undefined,
    sortOrder: 1 | -1 | undefined
  ): CaseInvestigationTrackerRow[] {
    if (!sortField) return rows;
    const order: 1 | -1 = sortOrder === -1 ? -1 : 1;
    return [...rows].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * order;
      if (bv == null) return 1 * order;
      return String(av).localeCompare(String(bv)) * order;
    });
  }
}

/** One row per employee so names/IDs vary across the table. */
const EMPLOYEE_POOL: ReadonlyArray<{ employeeId: string; employeeName: string }> = [
  { employeeId: 'CLD18', employeeName: 'Ahmad Ekstrom Bothman' },
  { employeeId: 'AJB10', employeeName: 'Davis Franci' },
  { employeeId: 'DRM22', employeeName: 'Dulce Rhiel Madsen' },
  { employeeId: 'TPA15', employeeName: 'Talan Passaquindici Arcand' },
  { employeeId: 'AGD09', employeeName: 'Ahmad Geidt' },
  { employeeId: 'KLM12', employeeName: 'Sofia Chen' },
  { employeeId: 'NOP14', employeeName: 'Marcus Webb' },
  { employeeId: 'QRS16', employeeName: 'Elena Vasquez' },
  { employeeId: 'TUV18', employeeName: 'James Okonkwo' },
  { employeeId: 'WXY20', employeeName: 'Priya Sharma' },
  { employeeId: 'ZAB22', employeeName: 'Oliver Bergstrom' },
  { employeeId: 'CDE24', employeeName: 'Yuki Tanaka' },
  { employeeId: 'FGH26', employeeName: 'Liam O\'Brien' },
  { employeeId: 'IJK28', employeeName: 'Zara Patel' },
  { employeeId: 'LMN30', employeeName: 'Noah Kim' },
];

function buildMockRows(): CaseInvestigationTrackerRow[] {
  const jobRoles = ['Nurse', 'Pharm Tec', 'CRNA', 'Pharmacist'];
  const caseStatuses: CaseStatus[] = [
    'New',
    'Pending Response',
    'Reviewed',
    'Active Investigation',
    'Resolved',
    'Unresolved',
    'Monitor',
    'Archived',
  ];
  const employeeStatuses: EmployeeStatus[] = [
    'Unassigned',
    'Referral',
    'Under Invest',
    'Rev In Progress',
    'Terminated',
    'Suspended',
    'Requires Review',
  ];
  const creators = ['Jamie Collins', 'Alex Rics', 'Jane Doe', 'John Smith'];
  const editors = ['Jane Doe', 'John Smith', 'Jamie Collins', 'Alex Rics'];

  const rows: CaseInvestigationTrackerRow[] = [];
  const totalRows = 50;

  for (let i = 0; i < totalRows; i++) {
    const emp = EMPLOYEE_POOL[i % EMPLOYEE_POOL.length];
    const created = new Date(2024, 6, 5 + (i % 20), 9 + (i % 12), 15 + (i % 45));
    const edited = new Date(2024, 2 + (i % 5), 1 + (i % 28), 8 + (i % 10), i % 60);
    rows.push({
      id: `case-${emp.employeeId}-${i + 1}`,
      caseId: i + 1,
      actionAlert: i % 4 === 0 ? 'attention' : 'none',
      caseStatus: caseStatuses[i % caseStatuses.length],
      employeeStatus: employeeStatuses[i % employeeStatuses.length],
      employee: emp.employeeName,
      employeeId: emp.employeeId,
      jobRole: jobRoles[i % jobRoles.length],
      createdDateTime: created.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
      createdBy: creators[i % creators.length],
      lastEditDate: edited.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      lastEditTime: edited.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      editedBy: editors[i % editors.length],
    });
  }

  return rows;
}
